import type { ReactNode } from 'react';
import type { UniversalTopbarProps } from '../components/UniversalTopbar';

type NotificationsRendererProps = {
  currentUrl: string;
  isLoggedIn: boolean;
  renderNotifications: (topbarProps: UniversalTopbarProps) => ReactNode;
};

type ShouldRender = (notificationsProps: NotificationsRendererProps) => boolean;

// This is a function that *makes* a render function. The reason for
// that is so the render function has access to `NotificationsRendererProps`
// which is necessary in some circumstances.
type MakeRenderNotifications = (
  notificationsProps: NotificationsRendererProps
) => (props: UniversalTopbarProps) => ReactNode;

type RendererConfig = {
  shouldRender: ShouldRender;
  makeRenderer: MakeRenderNotifications;
};

const renderDefaultNotifications: RendererConfig = {
  shouldRender: (renderProps: NotificationsRendererProps) => {
    return renderProps.isLoggedIn;
  },
  makeRenderer: (renderProps: NotificationsRendererProps) => (
    props: UniversalTopbarProps
  ) => {
    return renderProps.renderNotifications(props);
  },
};

const renderNothing: RendererConfig = {
  shouldRender: (renderProps: NotificationsRendererProps) => {
    return (
      !renderProps.isLoggedIn || renderProps.currentUrl.includes('/onboarding')
    );
  },
  makeRenderer: () => () => {
    return null;
  },
};

// Order-sensitive array of configurations to render notifications.
// The first one whose `shouldRender` returns `true` is used.
const configs: Array<RendererConfig> = [
  renderNothing,
  renderDefaultNotifications,
];

// Loop through `configs` to find the one that `shouldRender`,
// then return the renderer for it.
const findNotificationsRenderer = (renderProps: NotificationsRendererProps) => {
  const foundConfig = configs.find((config: RendererConfig) => {
    return config.shouldRender(renderProps);
  });
  return foundConfig && foundConfig.makeRenderer(renderProps);
};

export default findNotificationsRenderer;
